import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Divider, ExpandableBox } from '@optimus-web/core';
import { ExpandedContextProvider, useExpandedContext } from './context';
import { OptionsList } from './style';
import { TreeOptionEntry } from './TreeOptionEntry';
import { OptionProps } from './useOptions';
import { useToggleExpand } from './useToggleExpand';
import { SelectionStrategy } from './types';

type OptionsGroupProps = OptionProps & {
    defaultExpandedIds: Set<string>;
    defaultExpanded?: boolean;
    isSearching?: boolean;
    showDivider?: boolean;
    selectionStrategy: SelectionStrategy;
};

export const TreeOptionsGroup = forwardRef(
    (
        {
            id,
            showDivider,
            defaultExpanded,
            defaultExpandedIds,
            isSearching,
            selectionStrategy,
            ...optionProps
        }: OptionsGroupProps,
        ref
    ) => {
        const [expanded, setExpanded] = useState(defaultExpandedIds.has(id) || (defaultExpanded ?? false));
        const contextExpanded = useExpandedContext();

        const { ref: toggleRef, onToggleExpand: handleToggleExpand } = useToggleExpand();

        useEffect(() => {
            if (isSearching) {
                setExpanded(true);
            }
        }, [isSearching, setExpanded]);

        const treeBranchExpanded = expanded && contextExpanded;

        useImperativeHandle(
            ref,
            () => ({
                onToggleExpand: (e: boolean) => {
                    handleToggleExpand(e);
                    setExpanded(e);
                },
            }),
            [handleToggleExpand, setExpanded]
        );

        const baseProps = { defaultExpanded, defaultExpandedIds, isSearching };

        return (
            <>
                <TreeOptionEntry
                    key={`option-${id}`}
                    id={id}
                    selectionStrategy={selectionStrategy}
                    {...optionProps}
                    {...(optionProps.isGroup && {
                        expanded,
                        onToggleExpand: (e) => setExpanded(e),
                    })}
                />

                {'options' in optionProps ? (
                    <ExpandedContextProvider value={treeBranchExpanded}>
                        <li>
                            <ExpandableBox
                                aria-expanded={treeBranchExpanded}
                                as={OptionsList}
                                expanded={treeBranchExpanded}
                            >
                                {optionProps.options?.map((props, index) => (
                                    <TreeOptionsGroup
                                        key={props.id}
                                        ref={toggleRef(index)}
                                        selectionStrategy={selectionStrategy}
                                        {...baseProps}
                                        {...props}
                                    />
                                ))}
                            </ExpandableBox>
                        </li>
                    </ExpandedContextProvider>
                ) : null}

                {showDivider ? (
                    <li>
                        <Divider />
                    </li>
                ) : null}
            </>
        );
    }
);
